<template>
  <div class="login-container">
    <!-- Loading global (connexion) -->
    <div v-if="isLoading" class="loading">
      <div class="loading-box">
        <div class="spinner"></div>
        <p>Connexion en cours...</p>
      </div>
    </div>

    <!-- Logo -->
    <div class="logo-section">
      <a href="https://symbiocenter.fr/" target="_blank">
        <img :src="logo.src" alt="Logo Symbiofi" class="logo" />
      </a>
    </div>

    <!-- Contenu principal -->
    <div class="form-section" v-if="!isMaintenance">
      <!-- Notifications -->
      <div v-if="emailLoading" class="notification loading">
        <i class="fas fa-spinner fa-spin"></i>
        Recherche de l'email...
      </div>

      <div class="notification success" v-else-if="emailValidated && !userHasAcceptedCGU">
        <i class="fas fa-user-plus"></i>
        Bienvenue ! Créez votre mot de passe.
      </div>

      <div class="notification info" v-else-if="emailValidated && userHasAcceptedCGU">
        <i class="fas fa-sign-in-alt"></i>
        Bon retour ! Connectez-vous.
      </div>

      <!-- Formulaire -->
      <form @submit.prevent="connexion" class="form">
        <!-- Email -->
        <input
          class="input"
          :class="{ error: errorMail || errorMailCabinet }"
          type="email"
          placeholder="Adresse email"
          v-model="email"
          @input="verifyEmail"
          autocomplete="email"
        />

        <!-- Messages d'erreur -->
        <div class="error" v-if="errorMail && !errorMailCabinet">
          <i class="fas fa-exclamation-triangle"></i>
          Email non reconnu
        </div>

        <div class="error" v-if="!errorMail && errorMailCabinet">
          <i class="fas fa-building"></i>
          Compte cabinet - Contactez votre professionnel
        </div>

        <div class="error" v-if="errorLogIn">
          <i class="fas fa-times-circle"></i>
          Mot de passe incorrect
        </div>

        <div class="error" v-if="!userHasValidAccount">
          <i class="fas fa-envelope-open"></i>
          Compte non validé - Vérifiez vos emails
        </div>

        <div class="error" v-if="errorAbonnementExpire">
          <i class="fas fa-calendar-times"></i>
          Abonnement expiré - Contactez votre thérapeute
        </div>

        <!-- Mots de passe -->
        <transition name="fade">
          <div v-if="emailValidated" class="password-section">
            <!-- Nouveau mot de passe -->
            <template v-if="!userHasAcceptedCGU">
              <div class="password-input-container">
                <input
                  :type="showNewPassword ? 'text' : 'password'"
                  class="input password-input"
                  :class="{ error: passwordTooShort }"
                  placeholder="Nouveau mot de passe (min. 8 caractères)"
                  v-model="newPassword"
                />
                <button
                  type="button"
                  class="password-toggle"
                  @click="toggleNewPasswordVisibility"
                  tabindex="-1"
                >
                  <i :class="showNewPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
                </button>
              </div>

              <div class="password-input-container">
                <input
                  :type="showConfirmPassword ? 'text' : 'password'"
                  class="input password-input"
                  :class="{ error: passwordsDoNotMatch }"
                  placeholder="Confirmer le mot de passe"
                  v-model="confirmPassword"
                />
                <button
                  type="button"
                  class="password-toggle"
                  @click="toggleConfirmPasswordVisibility"
                  tabindex="-1"
                >
                  <i :class="showConfirmPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
                </button>
              </div>

              <div class="error" v-if="passwordsDoNotMatch">
                <i class="fas fa-key"></i>
                Les mots de passe ne correspondent pas
              </div>

              <div class="error" v-if="passwordTooShort">
                <i class="fas fa-lock"></i>
                8 caractères minimum requis
              </div>
            </template>

            <!-- Mot de passe existant -->
            <template v-else>
              <div class="password-input-container">
                <input
                  :type="showPassword ? 'text' : 'password'"
                  class="input password-input"
                  :class="{ error: errorLogIn }"
                  placeholder="Mot de passe"
                  v-model="password"
                />
                <button
                  type="button"
                  class="password-toggle"
                  @click="togglePasswordVisibility"
                  tabindex="-1"
                >
                  <i :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
                </button>
              </div>
            </template>
          </div>
        </transition>

        <!-- Bouton -->
        <button
          :disabled="isFormDisabled"
          type="submit"
          class="btn"
          :class="{ disabled: isFormDisabled }"
        >
          {{ buttonText }}
        </button>
      </form>

      <!-- Mot de passe oublié -->
      <div class="forgot">
        <router-link to="/reinitialisation-mot-de-passe" class="forgot-link">
          {{ $t("motDePasseOublie") }}
        </router-link>
      </div>
    </div>

    <!-- Maintenance -->
    <div v-else class="maintenance">
      <h2>Site en maintenance</h2>
      <p>Nous mettons tout en œuvre pour que vous retrouviez le site très vite.</p>
    </div>

    <!-- Composant changement de mot de passe -->
    <change-password
      :changePassword="changePassword"
      :email="email"
      @password-changed="handlePasswordChanged"
    />
  </div>
</template>

<script>
import { createNamespacedHelpers } from "vuex";
import Parse from "parse";
import ParseConfig from "@/api/parse.config";
import { getObjectById } from "@/services/parse/getObject.js";
import AuthService from "@/services/utilisateurs/authService.js";
import { resetIdPatientGlobal } from "@/services/utilisateurs/globalSettingsUser.js";

const User = createNamespacedHelpers("user");
import ChangePassword from "@/views/authentification/changePassword.vue";

export default {
  name: "LoginForm",
  data() {
    return {
      isMaintenance: false,
      email: "",
      password: "",
      newPassword: "",
      confirmPassword: "",
      emailValidated: false,
      userHasAcceptedCGU: false,
      emailLoading: false,
      errorMail: false,
      passwordsDoNotMatch: false,
      passwordTooShort: false,
      logo: {
        src: require("../../assets/img/logo_symbiofi.png"),
      },
      SessionToken: null,
      isLoading: false,
      errorMailCabinet: false,
      errorLogIn: false,
      userHasValidAccount: true,
      errorAbonnementExpire: false,
      changePassword: false,
      emailDebounceTimer: null,
      // Nouveaux états pour la visibilité des mots de passe
      showPassword: false,
      showNewPassword: false,
      showConfirmPassword: false,
    };
  },
  computed: {
    isFormDisabled() {
      if (!this.emailValidated) return true;
      if (!this.userHasAcceptedCGU) {
        return !this.isNewPasswordValid;
      }
      return !this.password;
    },
    isNewPasswordValid() {
      return this.newPassword &&
        this.confirmPassword &&
        this.newPassword === this.confirmPassword &&
        this.newPassword.length >= 8;
    },
    buttonText() {
      if (!this.emailValidated) {
        return this.$t("CONNEXION");
      }
      if (!this.userHasAcceptedCGU) {
        return "Créer mon mot de passe";
      }
      return this.$t("CONNEXION");
    }
  },
  watch: {
    newPassword() {
      this.validatePasswords();
    },
    confirmPassword() {
      this.validatePasswords();
    }
  },
  created() {
    resetIdPatientGlobal();
    this.resetPasswordSearch();
    try {
      this.recupToken();
    } catch (error) {
      console.log("Erreur lors de la récupération du token", error);
    }
  },
  components: {
    ChangePassword,
  },
  methods: {
    ...User.mapActions({
      actionsLogIn: "NewLogIn",
    }),
    // Nouvelles méthodes pour gérer la visibilité des mots de passe
    togglePasswordVisibility() {
      this.showPassword = !this.showPassword;
    },
    toggleNewPasswordVisibility() {
      this.showNewPassword = !this.showNewPassword;
    },
    toggleConfirmPasswordVisibility() {
      this.showConfirmPassword = !this.showConfirmPassword;
    },
    validatePasswords() {
      if (this.newPassword && this.confirmPassword) {
        this.passwordsDoNotMatch = this.newPassword !== this.confirmPassword;
      } else {
        this.passwordsDoNotMatch = false;
      }
      this.passwordTooShort = this.newPassword && this.newPassword.length < 8;
    },
    resetValidationState() {
      this.emailValidated = false;
      this.userHasAcceptedCGU = false;
      this.resetErrorStates();
    },
    resetErrorStates() {
      this.errorMail = false;
      this.errorMailCabinet = false;
      this.errorLogIn = false;
      this.passwordsDoNotMatch = false;
      this.passwordTooShort = false;
      this.errorAbonnementExpire = false;
    },
    handlePasswordChanged() {
      this.changePassword = false;
    },
    async verifyEmail() {
      if (!this.email) {
        this.emailValidated = false;
        this.userHasAcceptedCGU = false;
        this.errorMail = false;
        this.errorMailCabinet = false;
        this.errorLogIn = false;
        this.passwordsDoNotMatch = false;
        this.passwordTooShort = false;
        this.errorAbonnementExpire = false;
        return;
      }

      this.errorMail = false;
      this.errorMailCabinet = false;
      this.emailValidated = false;

      clearTimeout(this.emailDebounceTimer);
      this.emailDebounceTimer = setTimeout(async () => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(this.email)) {
          return;
        }

        this.emailLoading = true;
        this.errorMail = false;
        this.errorMailCabinet = false;
        this.errorLogIn = false;
        this.passwordsDoNotMatch = false;
        this.passwordTooShort = false;
        this.errorAbonnementExpire = false;

        ParseConfig();

        try {
          const result = await Parse.Cloud.run("verifyUserEmail", {
            email: this.email,
          });
          console.log("result", result);
          this.emailValidated = result.emailValidated;
          this.errorMail = result.errorMail;
          if (result.emailValidated) {
            this.userHasAcceptedCGU = result.userHasAcceptedCGU;
            this.idUser = result.idUser;
            this.userHasValidAccount = result.userHasValidAccount;
            this.errorMailCabinet = result.errorMailCabinet;
          }
        } catch (error) {
          console.error("Error verifying email:", error);
          this.emailValidated = false;
          this.errorMail = true;
        } finally {
          this.emailLoading = false;
        }
      }, 1000);
    },
    async connexion() {
      if (!this.emailValidated) return;

      try {
        this.isLoading = true;
        this.resetErrorStates();

        console.log("Connexion...");

        let result;
        if (!this.userHasAcceptedCGU) {
          console.log("Premier connexion - création du mot de passe");
          result = await this.handleFirstTimeLogin();
        } else {
          console.log("Connexion classique");
          result = await this.handleRegularLogin();
        }

        console.log("result", result);

        if (result && result.error && result.message && result.message.includes("abonnement a expiré")) {
          this.errorAbonnementExpire = true;
          this.errorLogIn = false;
        }

      } catch (error) {
        this.handleLoginError(error);
      } finally {
        console.log("Connexion terminée");
        if (Parse.User.current()) {
          this.isLoading = false;
        } else {
          this.isLoading = false;
          if (!this.errorAbonnementExpire) {
            this.errorLogIn = true;
          }
        }
      }
    },
    async handleFirstTimeLogin() {
      if (!this.isNewPasswordValid) {
        this.passwordsDoNotMatch = true;
        throw new Error("Mots de passe invalides");
      }
      await Parse.Cloud.run("changePassword", {
        email: this.email,
        confirmPassword: this.confirmPassword,
      });
      const result = await this.actionsLogIn({
        email: this.email,
        password: this.confirmPassword,
        SessionToken: this.SessionToken,
        MailResponse: this.MailResponse,
      });
      return result;
    },
    async handleRegularLogin() {
      const result = await this.actionsLogIn({
        email: this.email,
        password: this.password,
        SessionToken: this.SessionToken,
        MailResponse: this.MailResponse,
      });
      return result;
    },
    handleLoginError(error) {
      console.log("Erreur de connexion", error);
      if (error.message && error.message.includes("abonnement a expiré")) {
        this.errorAbonnementExpire = true;
        this.errorLogIn = false;
      } else {
        this.errorLogIn = true;
        this.errorAbonnementExpire = false;
      }
    },
    async resetPasswordSearch() {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const email = urlParams.get("email");
        const token = urlParams.get("token");
        console.log(email, token);
        var result = await Parse.Cloud.run("verify-email-token", {
          email: decodeURIComponent(email),
          token,
        });
        if (result.status === "success") {
          this.changePassword = true;
        }
        console.log("verify-email-token", result);
      } catch (error) {
        console.error("Erreur lors de la vérification de la maintenance", error);
      }
    },
    async recupToken() {
      try {
        var response = await AuthService.recupToken();
        console.log("Réponse:", response);
        this.SessionToken = response.tokenResponse;
        this.MailResponse = response.MailResponse;
        this.isLoading = true;
        var result = await this.actionsLogIn({
          SessionToken: this.SessionToken,
          MailResponse: this.MailResponse,
        });
        console.log("result", result);
      } catch (error) {
        console.log("Erreur lors de la récupération du token", error);
        if (error.message && error.message.includes("abonnement a expiré")) {
          this.errorAbonnementExpire = true;
        }
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.login-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  min-height: 70vh;
  max-width: 420px;
  margin: 0 auto;
  width: 100%;
}

/* Logo */
.logo-section {
  text-align: center;
  margin-bottom: 40px;
}

.logo {
  height: 60px;
  width: auto;
}

/* Form section */
.form-section {
  width: 100%;
}

/* Notifications */
.notification {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border-radius: 6px;
  margin-bottom: 20px;
  font-size: 14px;
  font-weight: 500;
}

.notification.loading {
  background: #e3f2fd;
  color: #1976d2;
}

.notification.success {
  background: #e8f5e8;
  color: #2e7d32;
}

.notification.info {
  background: #e0f7fa;
  color: #00796b;
}

/* Form */
.form {
  width: 100%;
}

/* Input */
.input {
  width: 100%;
  padding: 16px;
  border: 2px solid #e5e5e5;
  border-radius: 8px;
  font-size: 16px;
  margin-bottom: 16px;
  transition: border-color 0.2s;
}

.input:focus {
  outline: none;
  border-color: #239380;
}

.input.error {
  border-color: #f44336;
  background: #fef5f5;
}

.input::placeholder {
  color: #999;
}

/* Container pour les champs de mot de passe avec l'œil */
.password-input-container {
  position: relative;
  margin-bottom: 16px;
}

.password-input {
  padding-right: 50px; /* Espace pour le bouton œil */
  margin-bottom: 0; /* Reset margin pour le container */
}

.password-toggle {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  font-size: 16px;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
}

.password-toggle:hover {
  color: #239380;
}

.password-toggle:focus {
  outline: none;
  color: #239380;
}

/* Error messages */
.error {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #f44336;
  font-size: 14px;
  margin-bottom: 16px;
  padding: 12px;
  background: #fef2f2;
  border-radius: 6px;
}

/* Password section */
.password-section {
  margin-top: 8px;
}

/* Button */
.btn {
  width: 100%;
  padding: 16px;
  background: #239380;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 8px;
}

.btn:hover:not(.disabled) {
  background: #1e7a68;
  transform: translateY(-1px);
}

.btn.disabled {
  background: #ccc;
  cursor: not-allowed;
  transform: none;
}

/* Forgot password */
.forgot {
  text-align: center;
  margin-top: 20px;
}

.forgot-link {
  color: #239380;
  text-decoration: none;
  font-size: 14px;
}

.forgot-link:hover {
  text-decoration: underline;
}

/* Maintenance */
.maintenance {
  text-align: center;
  padding: 40px 20px;
}

.maintenance h2 {
  color: #333;
  margin-bottom: 16px;
  font-size: 24px;
}

.maintenance p {
  color: #666;
  line-height: 1.5;
}

/* Loading */
.loading {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.loading-box {
  background: white;
  padding: 40px;
  border-radius: 12px;
  text-align: center;
  max-width: 280px;
  width: 90%;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f0f0f0;
  border-top: 4px solid #239380;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

.loading-box p {
  color: #333;
  font-weight: 500;
  margin: 0;
}

/* Animations */
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}

/* Responsive */
@media (max-width: 480px) {
  .login-container {
    padding: 24px 16px;
  }

  .logo {
    height: 50px;
  }

  .input, .btn {
    padding: 14px;
  }

  .password-input {
    padding-right: 45px;
  }

  .password-toggle {
    right: 10px;
    font-size: 14px;
  }
}
</style>